import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader, Button, CardBody, Collapse, FormGroup, Label, Input } from "reactstrap";
import { ClearUpdateInstanceSettingsMergePersonsAtImport, GetInstanceSettingsMergePersonsAtImport, UpdateInstanceSettingsMergePersonsAtImport } from "../../../store/InstanceConfigAction";
import { useDispatch, useSelector } from "react-redux";
import { CoreInstanceSettingsMergePersonsAtImport } from "../../../models/CoreInstanceSettings";
import { EmptyRequestState, RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import StatusCode from "../../../util/StatusCode";
import Loading from "../../misc/Loading";
import SaveStatus from "../../misc/SaveStatus";
import { useTranslation } from "react-i18next";

interface MergePersonsProps {
    hasEditAccess: boolean
}

const MergePersons = ({ hasEditAccess }: MergePersonsProps) => {
    const { t } = useTranslation(["configuration", "common"]);
    const [showMergePersons, setShowMergePersons] = useState<boolean>(true);
    const [mergePersonsAtImportState, setMergePersonsAtImportState] = useState<CoreInstanceSettingsMergePersonsAtImport | undefined>(undefined);

    const dispatch = useDispatch();
    const mergePersonsAtImport = useSelector<RootState, RequestState<CoreInstanceSettingsMergePersonsAtImport>>(state => state.instanceSettings.mergePersonsAtImport);
    const updateMergePersonsAtImport = useSelector<RootState, EmptyRequestState>(state => state.instanceSettings.updateMergePersonsAtImport);

    useEffect(() => {
        GetInstanceSettingsMergePersonsAtImport()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (mergePersonsAtImport.code === StatusCode.COMPLETE) {
            setMergePersonsAtImportState(mergePersonsAtImport.data);
        }
    }, [mergePersonsAtImport]);

    useEffect(() => {
        if (updateMergePersonsAtImport.code === StatusCode.COMPLETE) {
            setTimeout(() => {
                ClearUpdateInstanceSettingsMergePersonsAtImport()(dispatch);
            }, 1500);
        }

        if (updateMergePersonsAtImport.code === StatusCode.ERROR) {
            setTimeout(() => {
                ClearUpdateInstanceSettingsMergePersonsAtImport()(dispatch);
                GetInstanceSettingsMergePersonsAtImport()(dispatch);
            }, 1500);
        }
    }, [dispatch, updateMergePersonsAtImport.code]);

    const hasUpdates = useMemo(() => {
        if (!mergePersonsAtImportState || mergePersonsAtImport.code !== StatusCode.COMPLETE) {
            return false;
        }

        return mergePersonsAtImportState.mergePersonsAtImport !== mergePersonsAtImport.data.mergePersonsAtImport;
    }, [mergePersonsAtImportState, mergePersonsAtImport]);

    const saveUpdates = () => {
        if (!mergePersonsAtImportState || !hasUpdates) {
            return;
        }

        UpdateInstanceSettingsMergePersonsAtImport(mergePersonsAtImportState)(dispatch);
    };

    const handleMergePersonsAtImportChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setMergePersonsAtImportState({ mergePersonsAtImport: event.target.checked });
    }, []);

    const disableSave = !hasUpdates || !hasEditAccess || updateMergePersonsAtImport.code === StatusCode.PENDING;

    console.log(mergePersonsAtImport)

    return (
        <Card>
            <CardHeader tag="h5">
                <Button outline color="secondary" style={{ width: "30px", float: "right", lineHeight: "0.8", padding: "0.5rem" }} onClick={() => setShowMergePersons(current => !current)}>{showMergePersons ? "-" : "+"}</Button>
                {t("instanceSettings.mergePeopleSection", { ns: "configuration" })}
            </CardHeader>
            <Collapse isOpen={showMergePersons}>
                <CardBody>
                    {mergePersonsAtImport.code === StatusCode.PENDING
                        ? <Loading />
                        : mergePersonsAtImport.code === StatusCode.COMPLETE ?
                            <>
                                <FormGroup check name="mergePersonsAtImport">
                                    <Label check style={{ display: "block", marginBottom: "8px" }}>
                                        <Input
                                            type="checkbox"
                                            name="mergePersonsAtImport"
                                            id="mergePersonsAtImport"
                                            disabled={!hasEditAccess}
                                            checked={mergePersonsAtImportState?.mergePersonsAtImport ?? false}
                                            onChange={handleMergePersonsAtImportChange}
                                        /> {t("instanceSettings.mergePeople", { ns: "configuration" })}
                                    </Label>
                                </FormGroup>

                                <div className="mt-3">
                                    <Button color="primary" disabled={disableSave} onClick={() => saveUpdates()}>
                                        {t("action.save", { ns: "common" })}
                                    </Button>
                                    <div style={{ marginLeft: "1rem", display: "inline-block" }}>
                                        <SaveStatus
                                            saveStatus={updateMergePersonsAtImport.code}
                                            successMsg={t("moduleView.saved", { ns: "configuration" })}
                                            errorMsg={t("error.somethingWentWrong", { ns: "common" })}
                                        />
                                    </div>
                                </div>
                            </>
                            : null}
                </CardBody>
            </Collapse>
        </Card>
    )
}

export default MergePersons;
